// src/components/common/Header.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, LogOut, Globe } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const Header = () => {
  const navigate = useNavigate();
  const { language, setLanguage, currentUser, setCurrentUser } = useApp();
  const t = translations[language];

  const languages = [
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'km', name: 'ខ្មែរ', flag: '🇰🇭' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];
  const [showLanguageMenu, setShowLanguageMenu] = React.useState(false);

  const handleLogout = () => {
    const confirmText = language === 'en' ? 'Are you sure you want to logout?' :
                       language === 'km' ? 'តើអ្នកប្រាកដថាចង់ចេញមែនទេ?' :
                       '确定要退出登录吗？';
    if (window.confirm(confirmText)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setCurrentUser(null);
      navigate('/login');
    }
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setShowLanguageMenu(false);
  };

  const getRoleName = (role) => {
    const map = {
      admin:   { zh: '管理员', en: 'Admin',    km: 'អ្នកគ្រប់គ្រង' },
      manager: { zh: '店长',   en: 'Manager',  km: 'អ្នកគ្រប់គ្រងហាង' },
      cashier: { zh: '收银员', en: 'Cashier',  km: 'អ្នកគិតលុយ' }
    };
    const lang = language === 'en' ? 'en' : language === 'km' ? 'km' : 'zh';
    return map[role]?.[lang] || role;
  };

  return (
    <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo 和标题 */}
          <div className="flex items-center space-x-3">
            <ShoppingCart className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">{t.title}</h1>
              <p className="text-sm text-blue-100">
                {currentUser?.store_name && (
                  <span>{currentUser.store_name} · </span>
                )}
                {currentUser?.name} · {getRoleName(currentUser?.role)}
              </p>
            </div>
          </div>

          {/* 右侧按钮 */}
          <div className="flex items-center space-x-3">
            {/* 语言切换 */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
              >
                <Globe className="w-5 h-5" />
                <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
                <span className="sm:hidden">{currentLanguage.flag}</span>
              </button>

              {showLanguageMenu && (
                <>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center space-x-2 ${
                          language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="font-medium">{lang.name}</span>
                        {language === lang.code && (
                          <span className="ml-auto text-blue-600">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="fixed inset-0 z-40" onClick={() => setShowLanguageMenu(false)} />
                </>
              )}
            </div>

            {/* 退出登录 */}
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">{t.logout}</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;